// ---------------------------------------------------------------------------
// SpeedyDock SDK — Debug instrumentation
//
// Opt-in, zero-cost-when-disabled telemetry for diagnosing the inline embed's
// resize→scroll behaviour on a live host page. Everything here no-ops unless
// debug is explicitly enabled, so production embeds are unaffected.
//
// Enable it on the live site WITHOUT changing the host's embed config:
//
//   localStorage.setItem("speedydock:debug", "1");   // then reload the page
//   // — or —
//   window.__SPEEDYDOCK_DEBUG__ = true;               // before SpeedyDock.init
//   // — or —  add ?sdkdebug=1 to the host page URL
//   // — or —  SpeedyDock.inline("#el", { debug: true })
//
// Each navigation cycle console.logs its measurements and appends a structured
// record to window.__SPEEDYDOCK_DEBUG_LOG__, so a full cycle can be inspected
// or exported after the fact (e.g. copy(window.__SPEEDYDOCK_DEBUG_LOG__)).
// ---------------------------------------------------------------------------

const STORAGE_KEY = "speedydock:debug";
const LOG_KEY = "__SPEEDYDOCK_DEBUG_LOG__";
const FLAG_KEY = "__SPEEDYDOCK_DEBUG__";
const PREFIX = "[SpeedyDock:debug]";

/** Force-enable/disable via an explicit option (e.g. inline({ debug: true })). */
let optIn = false;

export function setDebug(on: boolean): void {
  optIn = on;
}

/**
 * True when SDK debug telemetry should be emitted. Checks, in order: an
 * explicit option (setDebug), a window flag, localStorage, and a ?sdkdebug URL
 * param — so it can be flipped on live without touching the host embed config.
 */
export function isDebugEnabled(): boolean {
  if (optIn) return true;
  if (typeof window === "undefined") return false;

  const w = window as unknown as Record<string, unknown>;
  if (w[FLAG_KEY] === true) return true;

  try {
    if (window.localStorage?.getItem(STORAGE_KEY) === "1") return true;
  } catch {
    // localStorage can throw in sandboxed/blocked contexts — ignore.
  }

  try {
    if (new URLSearchParams(window.location.search).get("sdkdebug") === "1") {
      return true;
    }
  } catch {
    // Ignore malformed URLs.
  }

  return false;
}

/** High-resolution timestamp (falls back to Date.now when unavailable). */
export function now(): number {
  return typeof performance !== "undefined" &&
    typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

/** console.log a labelled group of telemetry when debug is enabled. */
export function debugLog(group: string, data: Record<string, unknown>): void {
  if (!isDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`${PREFIX} ${group}`, data);
}

/**
 * Append a structured record to window.__SPEEDYDOCK_DEBUG_LOG__ so a full
 * navigation cycle can be inspected or exported after the fact.
 */
export function debugRecord(record: Record<string, unknown>): void {
  if (!isDebugEnabled()) return;
  if (typeof window === "undefined") return;

  const w = window as unknown as Record<string, unknown[]>;
  if (!Array.isArray(w[LOG_KEY])) w[LOG_KEY] = [];
  w[LOG_KEY].push({ wallClock: Date.now(), ...record });
}
